import { NgClass } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	CardAccessComponent,
	INavHelp,
	ITagImage,
	ITagImageStatus,
	NavHelpComponent,
	ProgressComponent,
	PwcsLayoutComponent,
} from '@tecalisdev/infinity';
import { NavHelpAction } from 'src/app/core/models/navHelp.models';
import { AuthService } from 'src/app/core/services/auth.service';
import { ProgressService } from 'src/app/core/helpers/progress.service';
import { LanguageService } from 'src/app/core/services/language.service';
import { ModalScanQrComponent } from 'src/app/shared/components/modal-scan-qr/modal-scan-qr.component';
import { DesignService } from 'src/app/shared/services/config/design.service';
import { LanguagePickerUiService } from 'src/app/shared/services/language-picker-ui.service';
import { NavHelpService } from 'src/app/shared/services/nav-help.service';
import {
	CONNECTION_TYPE,
	IChooseOperation,
	IConnectionOperation,
	IDocumentsOperation,
	LocalizedText,
	IOperation,
	OPERATION_TYPE,
} from '../../models/user-identification';
import { ActivatedRoute } from '@angular/router';

@Component({
	selector: 'app-choose',
	imports: [
		TranslatePipe,
		NgClass,
		CardAccessComponent,
		ProgressComponent,
		PwcsLayoutComponent,
		NavHelpComponent,
		ModalScanQrComponent,
	],
	templateUrl: './choose.component.html',
	styleUrls: ['./choose.component.scss'],
})
export class ChooseComponent implements OnInit {
	private navHelpService = inject(NavHelpService);
	private languagePickerUi = inject(LanguagePickerUiService);
	public auth = inject(AuthService);

	@Input() operations: IOperation[];
	@Input()
	get operation(): IChooseOperation {
		return this._operation;
	}
	set operation(operation: IChooseOperation) {
		this._operation = operation;
	}
	public OPERATION_TYPE: any = OPERATION_TYPE;
	public CONNECTION_TYPE: any = CONNECTION_TYPE;
	/** Solo envío a otro dispositivo (QR) e idioma, como en tutorial NIF-97. */
	public navsHelp: Array<INavHelp> = this.navHelpService.getTutorialNavsHelp();
	public isVisibleScan: boolean = false;
	private _operation: IChooseOperation;

	constructor(
		public designService: DesignService,
		public progressService: ProgressService,
		private languageService: LanguageService,
		private trans: TranslateService,
		private route: ActivatedRoute,
	) {}

	ngOnInit(): void {
		this.route.queryParams.subscribe((params) => {
			if (params?.['bankredirect']) {
				const index = this.operation?.config?.operations?.findIndex(
					(op: IOperation) =>
						op.type === OPERATION_TYPE.CONNECTIONS && op.config.type === CONNECTION_TYPE.BANK_SCORING,
				);
				if (index > -1) {
					this.goToOperation(OPERATION_TYPE.CONNECTIONS, index);
				}
			}
		});
	}

	public goToOperation(type: OPERATION_TYPE, indexOp: number): void {
		const indexChoose = this.operations.findIndex((op: IOperation) => op.type === OPERATION_TYPE.CHOOSE);
		if (indexChoose > -1) {
			this.operations.splice(indexChoose + 1, 0, this.operation.config.operations[indexOp]);
			this.operation.completed = true;
		}
	}

	public docOperation(index: number): IDocumentsOperation {
		return this.operation?.config?.operations?.[index] as IDocumentsOperation;
	}

	public docTagImage(type: string, index: number): ITagImage {
		const icons = {
			['DNI']: () => 'icon-card',
			['PASSPORT']: () => 'icon-card',
			['NIE']: () => 'icon-card',
			['CERT_REG_CIU_UE']: () => 'icon-briefcase',
			['DRIVE_LICENSE']: () => 'icon-card',
			['IRPF']: () => 'icon-euro',
			['PAYROLL']: () => 'icon-card',
			['WORKLIFE']: () => 'icon-briefcase',
			['CHECK']: () => 'icon-card',
			['INVOICE']: () => 'icon-briefcase',
			['SCRIPTURE']: () => 'icon-briefcase',
			[OPERATION_TYPE.POL]: () => 'icon-user',
			[OPERATION_TYPE.OCR]: () => 'icon-card',
			[CONNECTION_TYPE.BANK_SCORING]: () => 'icon-bank',
			// [CONNECTION_TYPE.IRPF]: () => 'icon-briefcase',
			// [CONNECTION_TYPE.WORK_LIFE]: () => 'icon-agenda',
			// [CONNECTION_TYPE.SCRIPTURE]: () => 'icon-bills',
		};
		const status: Array<ITagImageStatus> = ['notice', 'control', 'default', 'level'];
		return { status: status[index % 4], icon: icons[type] ? icons[type]() : 'icon-file' };
	}

	public docText(type: string): { title: string; description: string } {
		const texts = {
			['DNI']: () => {
				return {
					title: this.trans.instant('CHOOSE.DNI_TITLE'),
					description: this.trans.instant('CHOOSE.DNI_DESCRIPTION'),
				};
			},
			['PASSPORT']: () => {
				return {
					title: this.trans.instant('CHOOSE.PASSPORT_TITLE'),
					description: this.trans.instant('CHOOSE.PASSPORT_DESCRIPTION'),
				};
			},
			['NIE']: () => {
				return {
					title: this.trans.instant('CHOOSE.NIE_TITLE'),
					description: this.trans.instant('CHOOSE.NIE_DESCRIPTION'),
				};
			},
			['CERT_REG_CIU_UE']: () => {
				return {
					title: this.trans.instant('CHOOSE.CERT_REG_CIU_UE_TITLE'),
					description: this.trans.instant('CHOOSE.CERT_REG_CIU_UE_DESCRIPTION'),
				};
			},
			['DRIVE_LICENSE']: () => {
				return {
					title: this.trans.instant('CHOOSE.DRIVE_LICENSE_TITLE'),
					description: this.trans.instant('CHOOSE.DRIVE_LICENSE_DESCRIPTION'),
				};
			},
			['IRPF']: () => {
				return {
					title: this.trans.instant('CHOOSE.IRPF_TITLE'),
					description: this.trans.instant('CHOOSE.IRPF_DESCRIPTION'),
				};
			},
			['PAYROLL']: () => {
				return {
					title: this.trans.instant('CHOOSE.PAYROLL_TITLE'),
					description: this.trans.instant('CHOOSE.PAYROLL_DESCRIPTION'),
				};
			},
			['WORKLIFE']: () => {
				return {
					title: this.trans.instant('CHOOSE.WORKLIFE_TITLE'),
					description: this.trans.instant('CHOOSE.WORKLIFE_DESCRIPTION'),
				};
			},
			['CHECK']: () => {
				return {
					title: this.trans.instant('CHOOSE.CHECK_TITLE'),
					description: this.trans.instant('CHOOSE.CHECK_DESCRIPTION'),
				};
			},
			['INVOICE']: () => {
				return {
					title: this.trans.instant('CHOOSE.INVOICE_TITLE'),
					description: this.trans.instant('CHOOSE.INVOICE_DESCRIPTION'),
				};
			},
			['SCRIPTURE']: () => {
				return {
					title: this.trans.instant('CHOOSE.SCRIPTURE_TITLE'),
					description: this.trans.instant('CHOOSE.SCRIPTURE_DESCRIPTION'),
				};
			},
			[CONNECTION_TYPE.BANK_SCORING]: () => {
				return {
					title: this.trans.instant('CHOOSE.BANK_SCORING_TITLE'),
					description: this.trans.instant('CHOOSE.BANK_SCORING_DESCRIPTION'),
				};
			},
			/*
			[CONNECTION_TYPE.IRPF]: () => {
				return {
					title: this.trans.instant('CHOOSE.IRPF_TITLE'),
					description: this.trans.instant('CHOOSE.IRPF_DESCRIPTION'),
				};
			},
			[CONNECTION_TYPE.WORK_LIFE]: () => {
				return {
					title: this.trans.instant('CHOOSE.WORKLIFE_TITLE'),
					description: this.trans.instant('CHOOSE.WORKLIFE_DESCRIPTION'),
				};
			},
			[CONNECTION_TYPE.SCRIPTURE]: () => {
				return {
					title: this.trans.instant('CHOOSE.SCRIPTURE_TITLE'),
					description: this.trans.instant('CHOOSE.SCRIPTURE_DESCRIPTION'),
				};
			},
			*/
		};
		return texts[type]
			? texts[type]()
			: { title: this.trans.instant('CHOOSE.'), description: this.trans.instant('CHOOSE.') };
	}

	public resolveText(value?: LocalizedText | null): string {
		return this.languageService.resolveLocalizedText(value);
	}

	get subtitle(): string {
		const connectionWithEval: IConnectionOperation = this.operation.config.operations.find(
			(op: IOperation) =>
				op.type === OPERATION_TYPE.CONNECTIONS &&
				(op.config.type === CONNECTION_TYPE.BANK_SCORING || op.config.evaluation),
		) as IConnectionOperation;
		const connectionWithoutEval: IConnectionOperation = this.operation.config.operations.find(
			(op: IOperation) =>
				op.type === OPERATION_TYPE.CONNECTIONS &&
				!(op.config.type === CONNECTION_TYPE.BANK_SCORING || op.config.evaluation),
		) as IConnectionOperation;
		const docs: IDocumentsOperation = this.operation.config.operations.find(
			(op: IOperation) => op.type === OPERATION_TYPE.DOCUMENTS,
		) as IDocumentsOperation;
		if (connectionWithEval) return this.trans.instant('CHOOSE.TITLE_EVAL');
		return this.trans.instant('CHOOSE.TITLE_MULTIPLE');
	}

	public getGridSize(): string {
		const numOpts = this.operation.config.operations.length;
		return numOpts > 1 ? 'grid-e' + (numOpts > 4 ? 3 : numOpts) : '';
	}

	public navHelpChange(action: INavHelp): void {
		const actions: Record<string, () => void> = {
			[NavHelpAction.QR]: () => {
				this.isVisibleScan = true;
			},
			[NavHelpAction.LANGUAGE]: () => {
				this.languagePickerUi.open();
			},
		};
		actions[action.id]?.();
	}
}
